/**
 * useCockpitContext — what the current page is about, for the KI-Assistent.
 *
 * - Machine page → machine number, type and line
 * - Line page    → line
 * - Elsewhere    → general (all data)
 *
 * `contextId` ("machine:<id>", "line:<id>", undefined = all data) ties a
 * conversation of the shared assistant (@digitalisation/enlixy-comp) to its
 * machine or line: coming back to that page reopens it. `contextString` is
 * what the agent receives as `instructions` in machine and line
 * conversations.
 *
 * `instructions` skip the chat API's guardrail scan (see CockpitService
 * streamChat), so the context string is only built here, from the route and
 * the SAP store, and an id is only used when it matches ID. Nothing the user
 * types can reach it.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Line, Machine } from '../services/BackendDataService';
import { useSAPDataStore } from '../store/SAPDataStore';

export type CockpitScope = 'machine' | 'line' | 'general';

export interface CockpitContext {
  scope: CockpitScope;
  /** "machine:<id>" or "line:<id>"; undefined = all data */
  contextId?: string;
  /** Context line for the agent (sent as `instructions` for machines and lines) */
  contextString: string;
  /** Machine number or line name for display; empty for all data */
  label: string;
}

/** SAP machine numbers, machine business keys (UUIDs) and line keys. */
const ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export const GENERAL_CONTEXT: CockpitContext = {
  scope: 'general',
  contextString: '[Context: Overview / Home page]',
  label: '',
};

/** contextId of a machine or line page; undefined when the id is not a plain SAP id. */
export function contextIdFor(scope: 'machine' | 'line', id: string): string | undefined {
  return ID.test(id) ? `${scope}:${id}` : undefined;
}

/** Scope and id of a contextId; null for all data and for anything malformed. */
export function parseContextId(
  contextId: string | undefined,
): { scope: 'machine' | 'line'; id: string } | null {
  const m = /^(machine|line):(.*)$/.exec(contextId ?? '');
  if (!m || !ID.test(m[2])) return null;
  return { scope: m[1] as 'machine' | 'line', id: m[2] };
}

const lineText = (line: Line): string =>
  line.linieNummer && line.linieName
    ? `${line.linieNummer} - ${line.linieName}`
    : line.linieName || line.lineId || '';

/** The context behind a contextId, described with the machines and lines of the SAP store. */
export function describeContext(
  contextId: string | undefined,
  machines: Machine[],
  lines: Line[],
): CockpitContext {
  const parsed = parseContextId(contextId);
  if (!parsed) return GENERAL_CONTEXT;
  const { scope, id } = parsed;
  const cid = `${scope}:${id}`;

  if (scope === 'machine') {
    const machine = machines.find(
      (m) => m.maschinennummer === id || m.maschineBK === id || m.machineId === id,
    );
    if (!machine) {
      return { scope, contextId: cid, contextString: `[Context: Machine: ${id}]`, label: id };
    }
    const line = lines.find((l) => l.linieBK === machine.lineId || l.lineId === machine.lineId);
    const machineNum = machine.maschinennummer || machine.maschineBK || '';
    // "20056 - Modell 401" -> "Modell 401"
    let machineType = machine.bautyp || machine.machineType || '';
    if (machineType && machineNum && machineType.startsWith(machineNum)) {
      machineType = machineType.slice(machineNum.length).replace(/^\s*[-–]?\s*/, '').trim();
    }
    const parts = [
      `Machine: ${machineNum}`,
      machineType ? `Type: ${machineType}` : '',
      line ? `Line: ${lineText(line)}` : '',
    ].filter(Boolean);
    return {
      scope,
      contextId: cid,
      contextString: `[Context: ${parts.join(', ')}]`,
      label: machineNum || id,
    };
  }

  const line = lines.find((l) => l.lineId === id || l.linieBK === id);
  if (!line) {
    return { scope, contextId: cid, contextString: `[Context: Line: ${id}]`, label: id };
  }
  return {
    scope,
    contextId: cid,
    contextString: `[Context: Line: ${lineText(line)}]`,
    label: line.linieName || id,
  };
}

/** "Machine: 20055", "Line: Linie 6" or "All Data" in the UI language (t of i18next). */
export function contextText(
  context: CockpitContext,
  t: (key: string, fallback: string) => string,
): string {
  const name = {
    machine: t('cockpit.contextMachine', 'Machine'),
    line: t('cockpit.contextLine', 'Line'),
    general: t('cockpit.contextGeneral', 'All Data'),
  }[context.scope];
  return context.label ? `${name}: ${context.label}` : name;
}

export function useCockpitContext(): CockpitContext {
  const { pathname } = useLocation();
  const machines = useSAPDataStore((s) => s.machines);
  const lines = useSAPDataStore((s) => s.lines);

  return useMemo(() => {
    const m = /^\/(machine|line)\/([^/]+)/.exec(pathname);
    const contextId = m ? contextIdFor(m[1] as 'machine' | 'line', m[2]) : undefined;
    return describeContext(contextId, machines, lines);
  }, [pathname, machines, lines]);
}
